import {
  AccountTypes,
  CountryUS,
  IndividualDesignationListedTypes,
  IRADesignationListedTypes,
  TransactionTypesEnum,
  YesNoEmpty,
} from '@interfaces/models';
import { JOINT_ACCOUNTS_TYPES } from '@interfaces/state';
import { createSelector } from '@ngrx/store';
import { isIndividualExecutor, isIRASingleExecutor } from '@src/app/secure/account-triage/helpers';
import { TaxClassification } from '@src/app/secure/terms/components/backup-withholding/backup-withholding.component';
import { TrustNumber } from '@src/app/shared/models/trust';
import { State, TransactionState } from '@store/index';
import {
  getTransactionsForTaxClassification,
  transactionsAdapter,
} from '@store/reducers/transaction-state/transaction-state.reducer';
import { getIsTrustToTrustFlow, TriageState } from '@store/reducers/triage/triage.reducer';
import { selectTrustState } from '@store/selectors/trust/trust.selectors';
import { selectTrustTaxInfoState } from '@store/selectors/trust-tax-info/trust-tax-info.selector';

const selectIsTrustToTrustFlowLocal = createSelector(
  (state: State) => state.triage as TriageState,
  (triage) => getIsTrustToTrustFlow(triage),
);

const { selectIds, selectEntities, selectAll, selectTotal } = transactionsAdapter.getSelectors();

export const selectStateTransactionIds = createSelector(
  (state: State) => state.transactions,
  (adaptedState) => selectIds(adaptedState),
);

export const selectAllTransactions = createSelector(
  (state: State) => state.transactions,
  (adaptedState) => !!Object.keys(adaptedState).length && selectAll(adaptedState),
);

export const selectTransactionsTotal = createSelector(
  (state: State) => state.transactions,
  (adaptedState) => selectTotal(adaptedState),
);

export const selectStateTransactions = createSelector(
  (state: State) => state.transactions,
  (adaptedState) => selectEntities(adaptedState),
);

export const selectTransactionById = (transactionId: string) =>
  createSelector(selectStateTransactions, (transactions) => transactions[transactionId]);

export const selectCurrentTransactionId = createSelector(
  (state: State) => state,
  (state) => state.currentTransactionId,
);

export const selectCurrentTransaction = createSelector(
  selectStateTransactions,
  selectCurrentTransactionId,
  (transactions, currentTransactionId) =>
    !!currentTransactionId ? transactions[currentTransactionId] : null,
);

export const selectCurrentTransactionExist = createSelector(
  selectCurrentTransaction,
  (transaction) => !!transaction,
);

export const selectIsFirstNewAccountTransactionExist = createSelector(
  selectAllTransactions,
  (transactions) =>
    Array.isArray(transactions) &&
    transactions.length > 0 &&
    transactions.filter((transaction: TransactionState) => transaction.firstNewAccount).length > 0,
);

export const selectIsCurrentTransactionFirstNewAccount = createSelector(
  selectCurrentTransaction,
  (transaction) => transaction?.firstNewAccount,
);

export const selectCurrentTransactionType = createSelector(
  selectCurrentTransaction,
  (transaction) =>
    !!transaction
      ? (transaction.selectedClientAccounts?.transactionType ?? TransactionTypesEnum.UNKNOWN)
      : TransactionTypesEnum.UNKNOWN,
);

export const selectIsCurrentTransactionNewAccountTransaction = createSelector(
  selectCurrentTransactionType,
  (transactionType) => transactionType === TransactionTypesEnum.NEW_ACCOUNT,
);

export const selectIsMultipleNewAccountTransactionExist = createSelector(
  selectAllTransactions,
  (transactions) =>
    Array.isArray(transactions) &&
    transactions.length > 1 &&
    transactions.filter(
      (transaction) =>
        transaction.selectedClientAccounts?.transactionType === TransactionTypesEnum.NEW_ACCOUNT,
    ).length > 1,
);

export const selectIsNewAccountTransactionExist = createSelector(
  selectAllTransactions,
  (transactions) =>
    Array.isArray(transactions) &&
    transactions.length > 0 &&
    Boolean(
      transactions.find(
        (transaction) =>
          transaction.selectedClientAccounts?.transactionType === TransactionTypesEnum.NEW_ACCOUNT,
      ),
    ),
);

export const selectNewAccountTransaction = createSelector(
  selectAllTransactions,
  (transactions) =>
    Array.isArray(transactions) &&
    transactions.length > 0 &&
    transactions.find(
      (transaction) =>
        transaction.selectedClientAccounts?.transactionType === TransactionTypesEnum.NEW_ACCOUNT,
    ),
);

export const selectCurrentTransactionInvestmentObjectives = createSelector(
  selectCurrentTransaction,
  (transaction) => transaction?.investmentObjectives ?? null,
);

export const selectCurrentTransactionSelectedAccounts = createSelector(
  selectCurrentTransaction,
  (transaction) => transaction?.selectedClientAccounts ?? null,
);

export const selectCurrentTransactionBeneficiaries = createSelector(
  selectCurrentTransaction,
  (transaction) => transaction?.beneficiaries ?? [],
);

export const selectCurrentTransactionTransferTo = createSelector(
  selectCurrentTransactionSelectedAccounts,
  (selectedAccount) => selectedAccount?.transferTo ?? null,
);

export const selectMultiAccountType = createSelector(
  selectCurrentTransaction,
  (transaction) => transaction?.accountTriage?.accountTypeInheriting ?? null,
);

export const selectMultiIraType = createSelector(
  selectCurrentTransactionTransferTo,
  (transferTo) => transferTo?.iraType ?? null,
);

export const selectMultiIraOption = createSelector(
  selectCurrentTransactionTransferTo,
  (transferTo) => transferTo?.iraOption ?? null,
);

export const selectIsReviewPageReachedAndEditMode = createSelector(
  (state: State) => state,
  (state) => state.isMultiReviewPageReachedAndEditMode ?? false,
);

export const selectIsOAReviewPageReachedAndEditMode = createSelector(
  (state: State) => state,
  (state) => state.isMultiOAReviewPageReachedAndEditMode ?? false,
);

export const selectCurrentTransactionHasOnlyTrustee = createSelector(
  selectCurrentTransaction,
  (transaction: TransactionState | null | undefined): boolean =>
    transaction?.accountTriage?.isOnlyTrustee === YesNoEmpty.YES,
);

export const selectCurrentTransactionHasOnlyExecutor = createSelector(
  selectCurrentTransaction,
  (transaction: TransactionState | null | undefined): boolean =>
    transaction?.accountTriage?.isOnlyExecutor === YesNoEmpty.YES,
);

export const selectAllTrustTransactions = createSelector(selectAllTransactions, (transactions) =>
  (transactions || []).filter(
    (transaction) =>
      (transaction.accountTriage.accountTypeInheriting === AccountTypes.IRA &&
        transaction.accountTriage.IRADesignation === IRADesignationListedTypes.TRUSTEE) ||
      ([AccountTypes.INDV, AccountTypes.JOINT].includes(
        transaction.accountTriage.accountTypeInheriting,
      ) &&
        transaction.accountTriage.individualDesignation ===
          IndividualDesignationListedTypes.TRUSTEE),
  ),
);

export const selectAllTransactionsWithSpouseRelationship = createSelector(
  selectAllTransactions,
  (transactions) =>
    (transactions || []).filter(
      (transaction) =>
        transaction?.accountTriage.isSpouse === 'YES' ||
        transaction?.accountTriage.isDecedentSpouse === YesNoEmpty.YES,
    ),
);

export const selectAllTransactionsWithBeneficiaries = createSelector(
  selectAllTransactions,
  (transactions) =>
    (transactions || []).filter((transaction) => {
      const { IRADesignation, isSpouse, individualDesignation } = transaction.accountTriage;
      const perStripes = IRADesignation === IRADesignationListedTypes.RELATIONSHIP;
      const individualOrJointBeneficiary =
        individualDesignation === IndividualDesignationListedTypes.BENEFICIARY && isSpouse === 'NO';
      const iraBeneficiary = IRADesignation === IRADesignationListedTypes.NAME && isSpouse === 'NO';
      return perStripes || individualOrJointBeneficiary || iraBeneficiary;
    }),
);

export const selectIndividualExecutorEstateTransactions = createSelector(
  selectAllTransactions,
  (transactions) =>
    (transactions || []).filter((transaction) => {
      const { estate, accountTriage } = transaction;

      return (
        isIndividualExecutor(accountTriage) && estate && estate.estateName && estate.tin_private
      );
    }),
);

export const selectIraExecutorEstateTransactions = createSelector(
  selectAllTransactions,
  (transactions) =>
    (transactions || []).filter((transaction) => {
      const { estate, accountTriage } = transaction;

      return (
        isIRASingleExecutor(accountTriage) && estate && estate.estateName && estate.tin_private
      );
    }),
);

const INDIVIDUAL_OR_JOINT_TYPES = [AccountTypes.INDV, ...JOINT_ACCOUNTS_TYPES];

export interface TrustInfoGroup {
  trustName: string;
  ein: string;
  grantorName: string;
  grantorSsn: string;
  isGrantorTrust: boolean;
  taxClassification: TaxClassification;
}

export interface EstateInfoGroup {
  estateName: string;
  tin: string;
}

export const selectDistinctNonW8TrustGroups = createSelector(
  selectAllTrustTransactions,
  (transactions): TrustInfoGroup[] => {
    const seen = new Set<string>();
    const groups: TrustInfoGroup[] = [];
    for (const t of transactions) {
      if (t.selectedClientAccounts?.transactionType !== TransactionTypesEnum.NEW_ACCOUNT) continue;
      if (
        t.trust?.countryQuestion === CountryUS.OTHER ||
        t.trust?.taxResidencyQuestion === CountryUS.OTHER ||
        t.trust?.grantorQuestion === CountryUS.OTHER
      )
        continue;
      const isGrantorTrust = t.trustTaxInfo?.numberQuestion === TrustNumber.SSN;
      const taxClassification = isGrantorTrust
        ? TaxClassification.Individual
        : TaxClassification.TRUST;
      const key = [
        t.trust?.trustName ?? '',
        t.trustTaxInfo?.ein ?? '',
        t.trustTaxInfo?.grantorName ?? '',
        t.trust?.trustDate ?? '',
      ].join('|');
      if (!seen.has(key)) {
        seen.add(key);
        groups.push({
          trustName: t.trust?.trustName ?? '',
          ein: t.trustTaxInfo?.ein ?? '',
          grantorName: t.trustTaxInfo?.grantorName ?? '',
          grantorSsn: t.trustTaxInfo?.ssn ?? '',
          isGrantorTrust,
          taxClassification,
        });
      }
    }
    return groups;
  },
);

export const selectDistinctNonW8EstateGroups = createSelector(
  selectIndividualExecutorEstateTransactions,
  selectIraExecutorEstateTransactions,
  (indvTxns, iraTxns): EstateInfoGroup[] => {
    const allEstateTxns = [...indvTxns, ...iraTxns];
    const seen = new Set<string>();
    const groups: EstateInfoGroup[] = [];
    for (const t of allEstateTxns) {
      if (t.selectedClientAccounts?.transactionType !== TransactionTypesEnum.NEW_ACCOUNT) continue;
      if (
        t.estate?.countryQuestion === CountryUS.OTHER ||
        t.estate?.taxResidencyQuestion === CountryUS.OTHER
      )
        continue;
      const key = `${t.estate?.estateName ?? ''}|${t.estate?.tin_private ?? ''}`;
      if (!seen.has(key)) {
        seen.add(key);
        groups.push({
          estateName: t.estate?.estateName ?? '',
          tin: t.estate?.tin_private ?? '',
        });
      }
    }
    return groups;
  },
);

export const selectHasIraNonEstateTrustTransaction = createSelector(
  selectAllTransactions,
  (transactions) =>
    Array.isArray(transactions) &&
    transactions.some(
      (t) =>
        t.selectedClientAccounts?.transactionType === TransactionTypesEnum.NEW_ACCOUNT &&
        t.accountTriage.accountTypeInheriting === AccountTypes.IRA &&
        t.accountTriage.IRADesignation !== IRADesignationListedTypes.EXECUTOR &&
        t.accountTriage.IRADesignation !== IRADesignationListedTypes.TRUSTEE,
    ),
);

export const selectHasIndividualOrJointNonEstateTrustTransaction = createSelector(
  selectAllTransactions,
  (transactions) =>
    Array.isArray(transactions) &&
    transactions.some(
      (t) =>
        t.selectedClientAccounts?.transactionType === TransactionTypesEnum.NEW_ACCOUNT &&
        INDIVIDUAL_OR_JOINT_TYPES.includes(t.accountTriage.accountTypeInheriting) &&
        t.accountTriage.individualDesignation !== IndividualDesignationListedTypes.EXECUTOR &&
        t.accountTriage.individualDesignation !== IndividualDesignationListedTypes.TRUSTEE,
    ),
);

export const selectTrustToTrustHasW8 = createSelector(
  selectTrustState,
  selectIsTrustToTrustFlowLocal,
  (trust, isTrustToTrust): boolean =>
    isTrustToTrust &&
    (trust.countryQuestion === CountryUS.OTHER ||
      trust.taxResidencyQuestion === CountryUS.OTHER ||
      trust.grantorQuestion === CountryUS.OTHER),
);

export const selectTrustToTrustNonW8Group = createSelector(
  selectTrustState,
  selectTrustTaxInfoState,
  selectIsTrustToTrustFlowLocal,
  (trust, trustTaxInfo, isTrustToTrust): TrustInfoGroup | null => {
    if (!isTrustToTrust) return null;
    if (
      trust.countryQuestion === CountryUS.OTHER ||
      trust.taxResidencyQuestion === CountryUS.OTHER ||
      trust.grantorQuestion === CountryUS.OTHER
    )
      return null;
    const isGrantorTrust = trustTaxInfo.numberQuestion === TrustNumber.SSN;
    return {
      trustName: trust.trustName ?? '',
      ein: trustTaxInfo.ein ?? '',
      grantorName: trustTaxInfo.grantorName ?? '',
      grantorSsn: trustTaxInfo.ssn ?? '',
      isGrantorTrust,
      taxClassification: isGrantorTrust ? TaxClassification.Individual : TaxClassification.TRUST,
    };
  },
);

export const selectAllNonW8TrustGroups = createSelector(
  selectDistinctNonW8TrustGroups,
  selectTrustToTrustNonW8Group,
  (groups, trustToTrustGroup): TrustInfoGroup[] =>
    trustToTrustGroup ? [...groups, trustToTrustGroup] : groups,
);

export const selectHasTrustTransaction = createSelector(
  selectAllNonW8TrustGroups,
  (groups) => groups.length > 0,
);

export const selectHasEstateTransaction = createSelector(
  selectDistinctNonW8EstateGroups,
  (groups) => groups.length > 0,
);

export const selectTrustHasW8 = createSelector(selectAllTrustTransactions, (transactions) =>
  transactions.some(
    (t) =>
      t.selectedClientAccounts?.transactionType === TransactionTypesEnum.NEW_ACCOUNT &&
      (t.trust?.countryQuestion === CountryUS.OTHER ||
        t.trust?.taxResidencyQuestion === CountryUS.OTHER ||
        t.trust?.grantorQuestion === CountryUS.OTHER),
  ),
);

export const selectEstateHasW8 = createSelector(selectAllTransactions, (transactions) =>
  (transactions || []).some(
    (t) =>
      (isIndividualExecutor(t.accountTriage) || isIRASingleExecutor(t.accountTriage)) &&
      t.estate?.estateName &&
      t.selectedClientAccounts?.transactionType === TransactionTypesEnum.NEW_ACCOUNT &&
      (t.estate?.countryQuestion === CountryUS.OTHER ||
        t.estate?.taxResidencyQuestion === CountryUS.OTHER),
  ),
);

export const selectHasW8Transaction = createSelector(
  selectTrustHasW8,
  selectEstateHasW8,
  selectTrustToTrustHasW8,
  (trustHasW8, estateHasW8, trustToTrustHasW8) => trustHasW8 || estateHasW8 || trustToTrustHasW8,
);

export const selectTrustInfo = createSelector(selectAllTrustTransactions, (transactions) => {
  const first = transactions[0];
  const isGrantorTrust = !!first?.trustTaxInfo?.grantorName;
  return {
    trustName: first?.trust?.trustName ?? '',
    trustDate: first?.trust?.trustDate ?? '',
    ein: first?.trustTaxInfo?.ein ?? '',
    grantorName: first?.trustTaxInfo?.grantorName ?? '',
    grantorSsn: first?.trustTaxInfo?.ssn ?? '',
    isGrantorTrust,
  };
});

export const selectEstateInfo = createSelector(
  selectIndividualExecutorEstateTransactions,
  selectIraExecutorEstateTransactions,
  (indvTxns, iraTxns) => {
    const first = [...indvTxns, ...iraTxns][0];
    return {
      estateName: first?.estate?.estateName ?? '',
      tin: first?.estate?.tin_private ?? '',
    };
  },
);

export const selectBackupWithholdingAnswer = (taxClassification: TaxClassification) =>
  createSelector(selectAllTransactions, (transactions): 'yes' | 'no' | null => {
    if (!Array.isArray(transactions)) return null;
    return (
      getTransactionsForTaxClassification(transactions, taxClassification)[0]
        ?.backupWithholdingAnswer ?? null
    );
  });
